var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
export var NA_COLOR = { bg: '#f0f0f0', text: '#999999' };
export var NO_DATA_COLOR = { bg: '#e0e0e0', text: '#666666' };
export var NA_LEGEND = __assign({ label: 'N/A' }, NA_COLOR);
export var NO_DATA = __assign({ label: 'No Data' }, NO_DATA_COLOR);
export var ACTIVE_COLOR = { bg: '#d4edda', text: '#155724' };
export var ACK_COMPLETE_COLOR = { bg: '#c3e6cb', text: '#155724' };
export var ACK_PARTIAL_COLOR = { bg: '#ffeeba', text: '#856404' };
export var ACK_NOT_STARTED_COLOR = { bg: '#f5c6cb', text: '#721c24' };
// Annual plan status — semantically distinct hues, WCAG AA contrast, colorblind-safe
export var ANNUAL_APPROVED_COLOR = { bg: '#c3e6cb', text: '#155724' }; // green  — success / complete
export var ANNUAL_SUBMITTED_COLOR = { bg: '#d1ecf1', text: '#0c5460' }; // teal   — submitted / awaiting review
export var ANNUAL_DRAFT_COLOR = { bg: '#ffeeba', text: '#856404' }; // amber  — work in progress
export var ANNUAL_NEW_COLOR = { bg: '#dbe7f3', text: '#1f3a5f' }; // blue-grey — not yet started
export var ANNUAL_REJECTED_COLOR = { bg: '#f5c6cb', text: '#721c24' }; // red    — rejected
export var CHAPTER_STATUS_LEGEND = [
    __assign({ label: 'Active' }, ACTIVE_COLOR),
    NA_LEGEND,
];
export var ACK_STATUS_LEGEND = [
    __assign({ label: 'Complete' }, ACK_COMPLETE_COLOR),
    __assign({ label: 'In Progress' }, ACK_PARTIAL_COLOR),
    __assign({ label: 'Not Started' }, ACK_NOT_STARTED_COLOR),
    NA_LEGEND,
];
export var ANNUAL_PLAN_STATUS_LEGEND = [
    __assign({ label: 'Approved' }, ANNUAL_APPROVED_COLOR),
    __assign({ label: 'Submitted' }, ANNUAL_SUBMITTED_COLOR),
    __assign({ label: 'Draft' }, ANNUAL_DRAFT_COLOR),
    __assign({ label: 'New' }, ANNUAL_NEW_COLOR),
    __assign({ label: 'Rejected' }, ANNUAL_REJECTED_COLOR),
    NO_DATA,
    NA_LEGEND,
];
/** Number of signatories required for a chapter to be fully acknowledged. */
export var TOTAL_SIGNATORIES = 3;
/** Fiscal year boundary: months before this value belong to the previous fiscal year. */
export var FY_START_MONTH = 4;
/** Storage keys — centralised to prevent magic-string scatter and aid testing. */
export var STORAGE_KEYS = {
    loginData: 'loginData',
    logOutFlag: 'logOutFlag',
    adminId: 'adminId',
    chapterId: 'chapterId',
    omniFiscalYear: 'omniFiscalYear',
};
/** Timing constants — defer to next tick so spinner/panel can paint before sync work. */
export var OMNI_TIMING = {
    dropdownRepositionMs: 0,
    exportSpinnerMs: 0,
};
export var NA_METRIC = { displayValue: 'N/A', color: NA_COLOR, value: null, signedCount: 0 };
export var NA_MATRIX_CELL = {
    chapterId: 0, siteName: '', bpgName: '',
    metrics: new Map([
        ['chapter-status', NA_METRIC],
        ['acknowledgement-status', NA_METRIC],
        ['annual-plan-status', NA_METRIC],
    ])
};
export function cellKey(siteName, bpgName) {
    return siteName + "_" + bpgName;
}
export function resolveVisibleSites(allSites, selectedSiteNames) {
    if (!selectedSiteNames || selectedSiteNames.length === 0) {
        return allSites;
    }
    var selectedNames = new Set(selectedSiteNames);
    return allSites.filter(function (site) { return selectedNames.has(site.siteName); });
}
export function resolveVisibleErgs(allErgs, selectedErgNames) {
    if (!selectedErgNames || selectedErgNames.length === 0) {
        return allErgs;
    }
    var selectedNames = new Set(selectedErgNames);
    return allErgs.filter(function (erg) { return selectedNames.has(erg.bpgName); });
}
export var OMNI_STRINGS = {
    pageTitle: 'Omnidashboard',
    toolbarAria: 'Matrix view and fiscal year',
    viewPillsAria: 'Matrix metric view',
    fyLabel: 'Fiscal year',
    legendAria: 'Cell color legend',
    selectAllSites: 'Select all sites',
    clearSiteSelection: 'Clear site selection',
    selectAllErgs: 'Select all ERGs',
    clearErgSelection: 'Clear ERG selection',
    exportButton: 'Export to Excel',
    exportAriaLabel: 'Export matrix to Excel spreadsheet',
    exportDisabledTitle: 'Adjust filters to export the matrix',
    genericErrorMessage: 'Something went wrong while loading omnidashboard data. Please try again.',
    emptyTitle: 'No data available',
    emptyHint: 'No chapters are available for this view. Try another metric or contact your administrator if this seems incorrect.',
    resetFilters: 'Reset filters',
    filterRegionAria: 'Filter by site and ERG',
    sitesLabel: 'Sites',
    sitesPlaceholder: 'All Sites',
    sitesHint: 'No selection includes all sites.',
    ergsLabel: 'ERGs',
    ergsPlaceholder: 'All ERGs',
    ergsHint: 'No selection includes all ERGs.',
    filterEmptyTitle: 'No combinations match your filters',
    filterEmptyHint: 'Broaden your site or ERG selection, or reset filters to restore the full matrix.',
    summaryFilteredHint: 'Status counts reflect the filtered view only.',
    cornerLabel: 'ERG × Site',
    announceNoMatch: 'No ERG–site combinations match your filters.',
    cellNaTooltip: 'No chapter for this ERG at this site.',
    cellOpenTooltip: 'Open chapter',
    ergSiteHeader: 'ERG \\ Site',
    clearSiteFilterAria: 'Clear site filter',
    clearErgFilterAria: 'Clear ERG filter',
};
export function omniSummaryLabel(visibleChapters, totalChapters, isFiltered) {
    var totalLabel = totalChapters + " " + (totalChapters === 1 ? 'chapter' : 'chapters');
    if (!isFiltered) {
        return 'Showing : ' + totalLabel;
    }
    return "Showing : " + visibleChapters + " of " + totalLabel;
}
export function omniContextInView(chapterCount) {
    return chapterCount + " chapters in view";
}
export function omniFilterContext(ergCount, siteCount) {
    return ergCount + " ERGs \u00D7 " + siteCount + " sites";
}
export function omniGridAriaLabel(viewLabel, fySuffix) {
    return viewLabel + " matrix, fiscal year FY" + fySuffix;
}
export function omniAnnounceFiltered(ergCount, siteCount, chapterCount) {
    return "Filtered view: " + ergCount + " ERGs, " + siteCount + " sites, " + chapterCount + " chapters shown.";
}
export function omniAnnounceAll(ergCount, siteCount, chapterCount) {
    return "Full matrix: " + ergCount + " ERGs, " + siteCount + " sites, " + chapterCount + " chapters shown.";
}
export function omniSelectionTooltipAll(count, plural) {
    return "All " + plural + " (" + count + ")";
}
export function omniFyChangeAnnounce(fySuffix) {
    return "Loading matrix for fiscal year FY" + fySuffix + ".";
}
